import { FeatureSystemId } from '../../../data/features';

export enum StoryScene {
  APP = 0,
  CONNECTED = 1,
  ORGANIZATION = 2,
  ADD_APPS = 3,
  MULTIPLE_APPS = 4,
  CLOUD_INTEGRATIONS = 5,
  TRANSFORMATION = 6,
  KEEP_BUILDING = 7
}

export const STORY_TIMELINE = {
  introScreenMs: 6300,
  prototype: {
    durationMs: 23500,
    sketch: { startMs: 0, durationMs: 1800 },
    build: { startMs: 3000, durationMs: 1200 },
    live: { startMs: 4600, durationMs: 1000 },
    handoff: { startMs: 8600, durationMs: 1200 },
    download: { startMs: 9800, durationMs: 800 },
    downloadProgress: { startMs: 10600, durationMs: 2000 },
    run: { startMs: 13400, durationMs: 800 },
    loadApp: { startMs: 14600, durationMs: 1600 },
    ready: { startMs: 16400, durationMs: 700 },
    completePicture: { startMs: 18000, durationMs: 1000 },
    camera: { sketchUntilMs: 2600, handoffUntilMs: 8600, runtimeUntilMs: 18000 },
    caption: { sketchUntilMs: 8600, downloadUntilMs: 13400, runtimeUntilMs: 16400 }
  },
  finale: {
    durationMs: 14500,
    simplifyMs: 1800,
    revealStartMs: 700,
    cycleStartMs: 3400,
    messageStartMs: 7400,
    messageDurationMs: 900
  },
  connected: {
    durationMs: 15500,
    workspace: { startMs: 3000, endMs: 8000, readyMs: 6200 },
    workflowStartMs: 9500,
    cloudOffset: { startMs: 3000, endMs: 8500, shifted: 80, default: 30 },
    camera: { closeUntilMs: 3000, wideUntilMs: 8500 },
    caption: { syncUntilMs: 3000, taskUntilMs: 8000 },
    connectionStartMs: 1000,
    workflowConnectionStartMs: 6500,
    syncOffsetStartMs: 300,
    taskOffsetStartMs: 6500,
    workspaceProgress: { buildStartMs: 3800, buildDurationMs: 2000, liveStartMs: 6000, liveDurationMs: 600 }
  }
} as const;

export const STORY_SCENES = [
  {
    id: StoryScene.APP,
    label: 'A prototype',
    heading: 'From an idea to a working app.',
    caption: 'Sketch a workflow. Build it in OXIDE. Download the Container, then run your app—even offline.',
    durationMs: STORY_TIMELINE.prototype.durationMs,
    camera: '-335 200 980 470'
  },
  {
    id: StoryScene.CONNECTED,
    label: 'Connected workflows',
    heading: 'Turn a small win into a catalyst for change.',
    caption:
      'An inspection becomes a report. A data change starts the next task. Work moves forward without another spreadsheet or manual handoff.',
    durationMs: STORY_TIMELINE.connected.durationMs,
    camera: '-20 170 1380 510'
  },
  {
    id: StoryScene.ORGANIZATION,
    label: 'Shared foundation',
    heading: 'Give every team a shared foundation.',
    caption: 'Bring more interfaces, teams, and workflows together—with shared data, access controls, and auditing.',
    durationMs: 5000,
    camera: '-20 0 1380 850'
  },
  {
    id: StoryScene.ADD_APPS,
    label: 'Add more apps',
    heading: 'Bring more of your business online.',
    caption: 'Start with inspections, then add warehouse and office apps as more teams move beyond manual processes.',
    durationMs: 5000,
    camera: '-470 0 1830 850'
  },
  {
    id: StoryScene.MULTIPLE_APPS,
    label: 'Apps working together',
    heading: 'Turn individual apps into connected operations.',
    caption:
      'Share data directly between apps, or use CloudCode to coordinate work across field, warehouse, and office teams.',
    durationMs: 5000,
    camera: '-20 10 1380 840'
  },
  {
    id: StoryScene.CLOUD_INTEGRATIONS,
    label: 'Cloud integrations',
    heading: 'Some work doesn’t need an app.',
    caption:
      'Connect cloud tasks to your apps—or run independent workflows between external systems, triggered by events or a schedule.',
    durationMs: 5000,
    camera: '-450 20 1810 840'
  },
  {
    id: StoryScene.TRANSFORMATION,
    label: 'Digital transformation',
    heading: 'From a prototype to a transformed business.',
    caption:
      'People, apps, and automated workflows working together. From capturing work in the field to coordinating operations and informing decisions.',
    durationMs: 6000,
    camera: '-490 -155 1880 1050'
  },
  {
    id: StoryScene.KEEP_BUILDING,
    label: 'Keep building',
    heading: 'A foundation for whatever comes next.',
    caption: 'Your apps and workflows are the starting point. Discover more of what you can build with JourneyApps.',
    durationMs: STORY_TIMELINE.finale.durationMs,
    camera: '-490 -155 1880 1050'
  }
] as const;

export interface StoryNode {
  system: FeatureSystemId;
  x: number;
  y: number;
  groupedY?: number;
  scene: StoryScene;
  detail: string;
}

export const STORY_NODES: readonly StoryNode[] = [
  { system: 'backend', x: 400, y: 340, scene: StoryScene.CONNECTED, detail: 'Data, files & synchronization' },
  { system: 'cloudcode', x: 770, y: 340, scene: StoryScene.CONNECTED, detail: 'Events → logic → action' },
  { system: 'oxide', x: 770, y: 110, groupedY: 50, scene: StoryScene.ORGANIZATION, detail: 'Visual editing + code' },
  {
    system: 'cli',
    x: 400,
    y: 110,
    groupedY: 50,
    scene: StoryScene.ORGANIZATION,
    detail: 'Your editor, Git & AI tools'
  },
  {
    system: 'sector',
    x: 400,
    y: 580,
    groupedY: 670,
    scene: StoryScene.ORGANIZATION,
    detail: 'Explore & manage live data'
  },
  {
    system: 'override',
    x: 770,
    y: 580,
    groupedY: 670,
    scene: StoryScene.ORGANIZATION,
    detail: 'Teams, policies & audit review'
  },
  { system: 'web-container', x: 45, y: 70, scene: StoryScene.ORGANIZATION, detail: 'Reach people in the browser' }
];

export const STORY_CONNECTIONS = [
  { id: 'sync', scene: StoryScene.CONNECTED, path: 'M265 410 H400', label: 'SYNC', x: 318, y: 394 },
  { id: 'event', scene: StoryScene.CONNECTED, path: 'M650 410 H770', label: 'TRIGGER', x: 710, y: 394 },
  {
    id: 'report',
    scene: StoryScene.CONNECTED,
    path: 'M1020 410 H1060 Q1080 410 1080 390 V330 Q1080 310 1100 310',
    label: '',
    x: 0,
    y: 0
  },
  {
    id: 'external',
    scene: StoryScene.CONNECTED,
    path: 'M1020 410 H1060 Q1080 410 1080 430 V500 Q1080 520 1100 520',
    label: '',
    x: 0,
    y: 0
  },
  {
    id: 'oxide',
    scene: StoryScene.ORGANIZATION,
    path: 'M895 250 V338 M889 330 L895 338 L901 330',
    label: 'BUILD & DEPLOY',
    x: 875,
    y: 298
  },
  {
    id: 'cli',
    scene: StoryScene.ORGANIZATION,
    path: 'M525 250 V338 M519 330 L525 338 L531 330',
    label: 'QUERY DATA',
    x: 545,
    y: 298
  },
  {
    id: 'ai-coding',
    scene: StoryScene.ORGANIZATION,
    path: 'M650 180 H768 M762 175 L768 180 L762 185',
    label: 'AI-assisted coding',
    x: 709,
    y: 164
  },
  { id: 'sector', scene: StoryScene.ORGANIZATION, path: 'M525 480 V580', label: '', x: 0, y: 0 },
  { id: 'override', scene: StoryScene.ORGANIZATION, path: 'M895 480 V580', label: '', x: 0, y: 0 },
  {
    id: 'browser',
    scene: StoryScene.ORGANIZATION,
    path: 'M170 210 V298 M164 290 L170 298 L176 290',
    label: '',
    x: 0,
    y: 0
  }
] as const;

export const STORY_APPS = [
  {
    x: 45,
    title: 'Field operations',
    kind: 'APP',
    detail: 'Inspect → synchronize → report',
    original: true
  },
  {
    x: 480,
    title: 'Warehouse app',
    kind: 'APP',
    detail: 'Receive → update stock → dispatch',
    original: false
  },
  {
    x: 915,
    title: 'Operations tracker',
    kind: 'APP',
    detail: 'Review → assign → coordinate',
    original: false
  }
] as const;

export const STORY_AUTOMATIONS = [
  { x: -410, y: 350, title: 'Data imports', source: 'ERP & inventory', result: 'Shared data' },
  {
    x: -410,
    y: 600,
    title: 'Order orchestration',
    source: 'Orders API',
    result: 'Fulfillment API'
  },
  {
    x: -410,
    y: 100,

    title: 'Automated reporting',
    source: 'Finance database',
    result: 'Email reports'
  }
] as const;

export const STORY_SHARED_TOOLS: readonly FeatureSystemId[] = ['oxide', 'override', 'sector', 'cli'];

export const STORY_SHARED_CONNECTIONS = [
  'M525 190 V227 M895 190 V227 M235 265 V227 H1105 V265 M670 227 V265',
  'M235 595 V632 H1105 V595 M670 595 V632 M525 632 V670 M895 632 V670'
] as const;
